import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@components/utils/utils";
import { Button } from "@components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";

export interface MultiSelectPopoverOption {
  label: string;
  value: string;
}

export interface MultiSelectPopoverProps {
  options: MultiSelectPopoverOption[];
  selected: string[];
  onSelectedChange: (values: string[]) => void;
  placeholder?: string;
  selectedLabel?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
}

export const MultiSelectPopover = ({
  options,
  selected,
  onSelectedChange,
  placeholder = "Sélectionner...",
  selectedLabel,
  searchPlaceholder = "Rechercher...",
  emptyText = "Aucun résultat",
  disabled,
  className,
}: MultiSelectPopoverProps) => {
  const [open, setOpen] = useState(false);
  const selectedCount = options.filter(o => selected.includes(o.value)).length;

  const triggerLabel =
    selectedCount > 0 && selectedLabel
      ? `${selectedCount} ${selectedLabel}${selectedCount > 1 ? "s" : ""}`
      : selectedCount > 0
        ? `${selectedCount} sélectionné${selectedCount > 1 ? "s" : ""}`
        : placeholder;

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn("h-9 min-w-[140px] justify-between", className)} disabled={disabled}>
          <span className="truncate text-xs">{triggerLabel}</span>
          <ChevronsUpDown className="ml-1 size-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.length > 1 && (
                <CommandItem
                  onSelect={() => {
                    const allValues = options.map(o => o.value);
                    const allSelected = allValues.every(v => selected.includes(v));
                    onSelectedChange(allSelected ? [] : allValues);
                  }}
                >
                  <div
                    className={cn(
                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                      options.every(o => selected.includes(o.value))
                        ? "bg-primary text-primary-foreground"
                        : "opacity-50 [&_svg]:invisible",
                    )}
                  >
                    <Check className="size-3" />
                  </div>
                  <span className="font-medium">Tout sélectionner</span>
                </CommandItem>
              )}
              {options.map(option => {
                const isSelected = selected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const next = isSelected ? selected.filter(v => v !== option.value) : [...selected, option.value];
                      onSelectedChange(next);
                    }}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <Check className="size-3" />
                    </div>
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
